import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import RadioButton from '../../../../components/forms/RadioButton';
import Checkbox from '../../../../components/forms/Checkbox';
import StatusLabel from '../../../../components/forms/StatusLabel';
import {
  onRequestRuleEditRoles,
  OnRequestRuleModel,
  OnRequestRuleRequest
} from '../../../../api/pricing/pricing.types';
import { SelectedOption, StatusType } from '../../../../api/api.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { CarClassModel, DeskModel } from '../../../../api/core/core.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { OnRequestRuleActionTypes } from '../../types/onrequestrule.types';
import {
  addNewONRequestRule,
  deleteOnRequestRule,
  editOnRequestRule,
  getOnRequestRule
} from '../../actions/onrequestrule.actions';
import Select from '../../../../components/forms/Select';
import { getCarClassesList, getLocationsList } from '../../../../helpers/listview.helper';
import { TwoColumnsSelectValueModel } from '../../../../components/forms/TwoColumnsSelect';
import { MultiSelect } from '../../../../components/forms/MultiSelect';
import CalendarDateRangePicker, { DateRangeInputNew } from '../../../../components/forms/CalendarDateRangePicker';
import dayjs from 'dayjs';
import { dateOrderCorrect, dateTimeOrderCorrect, formatDateTime } from '../../../../helpers/date.helper';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import { Link } from 'react-router-dom';


interface OnRequestRuleLocationsProps {
  id: string;
}

interface OnRequestRuleDetailsProps extends RouteComponentProps<OnRequestRuleLocationsProps>, WithTranslation {
  selectedOnRequestRule: OnRequestRuleModel | null;
  getSelectedOnRequestRule: (id?: number) => Promise<any>;
  editOnRequestRule: (id: number, request: OnRequestRuleRequest) => void;
  addNewOnRequestRule: (request: OnRequestRuleRequest) => void;
  deleteOnRequestRule: (id: number) => void;
  getAllCarClasses: () => void;
  getDeskList: () => void;
  isNew?: boolean;
  postSuccess: boolean | null;
  carClasses: CarClassModel[];
  desks: DeskModel[];
  lastFetch: number | null;
  user: UserModel | null;
}

interface OnRequestRuleDetailsState {
  isModalOpen: boolean;
  isEdit: boolean;
  selectedCarClasses: string[];
  selectedLocations: string[];
}

class OnRequestRuleDetails extends React.Component<OnRequestRuleDetailsProps, OnRequestRuleDetailsState> {

  readonly state = {
    isModalOpen: false,
    isEdit: false,
    selectedCarClasses: [],
    selectedLocations: []
  };

  form = FormBuilder.group({
    id: [ null ],
    locationsSelection: [ 'all' ],
    carClassSelection: [ 'all' ],
    validFrom: [ null, Validators.required ],
    validTo: [ null, Validators.required ],
    validFromTime: [ '00:00', Validators.required ],
    validToTime: [ '23:59', Validators.required ],
    locations: [ { items: [], selected: [] } ],
    carClasses: [ { items: [], selected: [] } ],
    active: [ true ]
  });

  async componentDidMount() {
    if (!this.props.carClasses.length) {
      this.props.getAllCarClasses();
    }
    if (!this.props.desks.length) {
      this.props.getDeskList();
    }

    const { id } = this.props.match.params;
    await this.props.getSelectedOnRequestRule(id ? Number(id) : undefined);

    if (this.props.carClasses.length) {
      this.assignCarClassesToForm(this.props.carClasses);
    }
    if (this.props.desks.length) {
      this.assignDesksToForm(this.props.desks);
    }
  }

  componentDidUpdate(prevProps: OnRequestRuleDetailsProps) {
    if ((this.props.isNew) && this.props.postSuccess && this.props.selectedOnRequestRule) {
      this.props.history.push(`/rates/onrequestrules/${this.props.selectedOnRequestRule.id}/view`);
    }
    if (this.props.selectedOnRequestRule && (!prevProps.selectedOnRequestRule || this.props.selectedOnRequestRule !== prevProps.selectedOnRequestRule)) {
      this.updateFormValue();
    }
    if (this.props.carClasses.length !== prevProps.carClasses.length) {
      this.assignCarClassesToForm(this.props.carClasses);
    }
    if (this.props.desks.length !== prevProps.desks.length) {
      this.assignDesksToForm(this.props.desks);
    }
  }

  updateFormValue = () => {
    if (!this.props.selectedOnRequestRule) {
      return;
    }
    this.form.patchValue({
      locationsSelection: this.props.selectedOnRequestRule.onRequestRuleDesks.length ? 'selection' : 'all',
      carClassSelection: this.props.selectedOnRequestRule.onRequestRuleCarClasses.length ? 'selection' : 'all',
      validFrom: dayjs(this.props.selectedOnRequestRule.validFrom),
      validTo: dayjs(this.props.selectedOnRequestRule.validTo),
      validFromTime: (this.props.selectedOnRequestRule.validFrom).split('T')[ 1 ].slice(0, 5),
      validToTime: (this.props.selectedOnRequestRule.validTo).split('T')[ 1 ].slice(0, 5),
      locations: this.props.selectedOnRequestRule ? {
        ...this.form.controls.locations.value,
        selected: this.props.selectedOnRequestRule.onRequestRuleDesks.map((c) => c.deskCode)
      } : this.form.controls.locations.value,
      carClasses: this.props.selectedOnRequestRule ? {
        ...this.form.controls.carClasses.value,
        selected: this.props.selectedOnRequestRule.onRequestRuleCarClasses.map((c) => c.acrissCode)
      } : this.form.controls.carClasses.value,
      active: this.props.selectedOnRequestRule.statusType === StatusType.ACTIVE
    });

    this.setState({
      selectedCarClasses: this.props.selectedOnRequestRule.onRequestRuleCarClasses.map((c) => c.acrissCode),
      selectedLocations: this.props.selectedOnRequestRule.onRequestRuleDesks.map((c) => c.deskCode)
    });
  };

  populateTimeSelect = () => {
    const times: { value: string; label: string }[] = [];
    for (let i = 0; i < 24; i += 1) {
      if (i < 10) {
        times.push({ value: (`0${String(i)}:00`), label: (`0${String(i)}:00`) });
        times.push({ value: (`0${String(i)}:30`), label: (`0${String(i)}:30`) });
      } else {
        times.push({ value: (`${String(i)}:00`), label: (`${String(i)}:00`) });
        times.push({ value: (`${String(i)}:30`), label: (`${String(i)}:30`) });
      }
    }

    return times;
  };

  assignDesksToForm = (desks: DeskModel[]) => {
    const form = this.form.controls.locations.value as TwoColumnsSelectValueModel;
    desks.forEach((desk) => {
      const obj: any = { ...desk };
      obj.deskCode = desk.code;

      form.items.push(obj);
    });
    if (this.props.selectedOnRequestRule) {
      this.props.selectedOnRequestRule.onRequestRuleDesks.forEach((d) => {
        form.selected.push(d.deskCode);
      });
      this.setState({
        selectedLocations: this.props.selectedOnRequestRule.onRequestRuleDesks.map((d) => d.deskCode)
      });
    }
  };

  assignCarClassesToForm = (carClasses: CarClassModel[]) => {
    const prev = this.form.controls.carClasses.value as TwoColumnsSelectValueModel;
    const uniqueCarClasses = [];
    const seen = new Set();
    for (const item of carClasses) {
      if (!seen.has(item.acrissCode)) {
        seen.add(item.acrissCode);
        uniqueCarClasses.push(item);
      }
    }
    prev.items = uniqueCarClasses;
    if (this.props.selectedOnRequestRule) {
      this.props.selectedOnRequestRule.onRequestRuleCarClasses.forEach((c) => {
        prev.selected.push(c.acrissCode);
      });
      this.setState({
        selectedCarClasses: this.props.selectedOnRequestRule.onRequestRuleCarClasses
          .map((c) => c.acrissCode)
      });
    }
  };

  onAllCarClassesSelect = () => {
    this.form.controls.carClasses.setValue({ ...this.form.controls.carClasses.value, selected: [] });
    this.setState({ selectedCarClasses: [] });
  };

  onAllLocationsSelect = () => {
    this.form.controls.locations.setValue({ ...this.form.controls.locations.value, selected: [] });
    this.setState({ selectedLocations: [] });
  };

  onDeleteConfirm = () => {
    const { id } = this.props.match.params;
    this.props.deleteOnRequestRule(Number(id));
    this.props.history.push('/rates/onrequestrules');
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (!dateOrderCorrect({ startDate: form.validFrom.value, endDate: form.validTo.value }, 'valid',
      this.form, true)) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    const startTime = form.validFromTime.value.value ? form.validFromTime.value.value : form.validFromTime.value;
    const endTime = form.validToTime.value.value ? form.validToTime.value.value : form.validToTime.value;
    const dateTimeCheck = dateTimeOrderCorrect({
      startDate: form.validFrom.value,
      endDate: form.validTo.value, startTime: startTime, endTime: endTime
    }, 'valid', this.form);
    if (dateTimeCheck.error) {
      return;
    }

    const request: OnRequestRuleRequest = {
      validFrom: dateTimeCheck.startDateTime,
      validTo: dateTimeCheck.endDateTime,
      onRequestRuleDesks: this.state.selectedLocations.map((v: string) => ({ deskCode: v })),
      onRequestRuleCarClasses: this.state.selectedCarClasses.map((v: string) => ({ acrissCode: v })),
      statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE
    };

    if (this.state.isEdit && this.props.selectedOnRequestRule) {
      const { id } = this.props.match.params;
      this.props.editOnRequestRule(Number(id), request);
      this.setState({ isEdit: false });

    } else {
      this.props.addNewOnRequestRule(request);
    }
  };

  patchField = (option: SelectedOption, field: string) => {
    if (field === 'validFromTime') {
      dateTimeOrderCorrect({
        startDate: this.form.controls.validFrom.value,
        endDate: this.form.controls.validTo.value, startTime: option.value,
        endTime: this.form.controls.validToTime.value
      }, 'valid', this.form);
    } else if (field === 'validToTime') {
      dateTimeOrderCorrect({
        startDate: this.form.controls.validFrom.value,
        endDate: this.form.controls.validTo.value, startTime: this.form.controls.validFromTime.value,
        endTime: option.value
      }, 'valid', this.form);
    }
    this.form.patchValue({ [ field ]: option.value });
  };

  onCancel = () => {
    if (this.props.isNew) {
      this.props.history.push('/rates/onrequestrules/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  onDateRangeChange = (range: DateRangeInputNew, field: string) => {
    dateOrderCorrect({ startDate: range.startDate, endDate: range.endDate }, 'valid', this.form,
      true);
    dateTimeOrderCorrect({
      startDate: range.startDate,
      endDate: range.endDate, startTime: this.form.controls.validFromTime.value,
      endTime: this.form.controls.validToTime.value
    }, 'valid', this.form);
    this.form.patchValue({
      [ `${field}From` ]: range.startDate,
      [ `${field}To` ]: range.endDate
    });
  };

  handleCarClassSelectValueChange = (v: any) => {
    this.setState({ selectedCarClasses: v.map((itm: any) => itm.acrissCode) });
  };

  handleLocationSelectValueChange = (v: any) => {
    this.setState({ selectedLocations: v.map((itm: any) => itm.deskCode) });
  };

  render() {
    if (!this.props.isNew && !this.props.selectedOnRequestRule) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNew;
    const { t } = this.props;

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/rates', label: 'Rates' },
              { path: '/rates/onrequestrules', label: 'On Request Rules' },
              { path: null, label: 'On Request Rule Details' }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{this.props.isNew ? 'Add new on request rule' : 'On request rule details'}</h1>
              {!!this.props.selectedOnRequestRule && !showForm && this.props.user && userCanAccessComponent(this.props.user, onRequestRuleEditRoles)
                && (
                  <div>
                    <button type="button" className="btn btn-danger mr-2"
                            onClick={() => this.setState({ isModalOpen: true })}>
                      {t('b.delete_Generic delete button').toUpperCase()}</button>
                    <button type="button" className="btn btn-primary edit-button" onClick={() => this.onEdit()}>
                      {t('b.edit_Generic edit button').toUpperCase()}
                    </button>
                  </div>
                )}
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal pt-1" onSubmit={(e) => this.onSubmit(e)}>
                <SectionTitle title={t('section_title.on_request_rule.on_request_rule').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.on_request_rule.valid_period')}:</label>
                  </div>
                  {
                    this.props.selectedOnRequestRule && !showForm
                    && (
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <div className="form-control-static">
                          {this.form.controls.validFrom.value && this.form.controls.validFrom.value.format('DD.MM.YYYY')}
                          {' - '}
                          {this.form.controls.validTo.value && this.form.controls.validTo.value.format('DD.MM.YYYY')}
                        </div>
                      </div>
                    )
                  }
                  {
                    showForm
                    && (
                      <>
                        <div className="col-4 col-sm-2">
                          <CalendarDateRangePicker
                            startDate={this.form.controls.validFrom.value}
                            endDate={this.form.controls.validTo.value}
                            inputStyle={{ flex: '50%' }}
                            isStartDateInput
                            isDateTimeInput
                            id="1"
                            onChange={(val) => this.onDateRangeChange(val, 'valid')}
                            isInvalid={this.form.controls.validFrom.invalid && this.form.submitted}/>
                        </div>
                        <div className="col-4 col-sm-2 pl-0 pr-3">
                          <FieldControl
                            strict={false}
                            name="validFromTime"
                            render={(props) => (
                              <Select
                                defaultValue={{
                                  value: this.form.controls.validFromTime.value,
                                  label: String(this.form.controls.validFromTime.value)
                                }}
                                className={`mr-1 ${this.form.controls.validFromTime.invalid ? 'invalid' : ''}`}
                                placeholder="Choose start time"
                                options={(this.populateTimeSelect())}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="valid from time"
                                onChange={(value) => this.patchField(value as SelectedOption, 'validFromTime')}
                              />
                            )}
                          />
                        </div>
                      </>
                    )
                  }
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                  </div>
                  {
                    this.props.selectedOnRequestRule && !showForm
                    && (
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <div className="form-control-static">
                          {this.form.controls.validFromTime.value}
                          {' - '}
                          {this.form.controls.validToTime.value}
                        </div>
                      </div>
                    )
                  }
                  {
                    showForm
                    && (
                      <>
                        <div className="col-4 col-sm-2">
                          <CalendarDateRangePicker
                            startDate={this.form.controls.validFrom.value}
                            endDate={this.form.controls.validTo.value}
                            inputStyle={{ flex: '50%' }}
                            isStartDateInput={false}
                            id="2"
                            isDateTimeInput
                            onChange={(val) => this.onDateRangeChange(val, 'valid')}
                            isInvalid={this.form.controls.validTo.invalid && this.form.submitted}/>
                        </div>
                        <div className="col-4 col-sm-2 pl-0 pr-3">
                          <FieldControl
                            strict={false}
                            name="validToTime"
                            render={(props) => (
                              <Select
                                defaultValue={{
                                  value: this.form.controls.validToTime.value,
                                  label: String(this.form.controls.validToTime.value)
                                }}
                                className={`mr-1 ${this.form.controls.validToTime.invalid ? 'invalid' : ''}`}
                                placeholder="Choose end time"
                                options={(this.populateTimeSelect())}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="end time"
                                onChange={(value) => this.patchField(value as SelectedOption, 'validToTime')}
                              />
                            )}
                          />
                        </div>
                      </>
                    )
                  }
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.on_request_rule.status')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedOnRequestRule && !showForm
                      && (
                        <div className="form-control-static">
                          <StatusLabel status={this.props.selectedOnRequestRule.statusType}/>
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="active"
                          render={(props) => <Checkbox {...props} id="statusField" className="mt-1">
                            {t('text.status.active')}</Checkbox>}
                        />
                      )
                    }
                  </div>
                </div>
                <SectionTitle title={t('section_title.on_request_rule.desks').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4"/>
                  <div className="col-8 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedOnRequestRule && !showForm
                      && (
                        <div className="form-control-static">
                          {getLocationsList(this.props.selectedOnRequestRule.onRequestRuleDesks, this.props.desks,
                            false, this.props.history)}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <>
                          <div className="d-flex flex-column flex-wrap">
                            <div className="pb-1">
                              <FieldControl
                                strict={false}
                                name="locationsSelection"
                                render={(props) => <RadioButton {...props}
                                                                onClick={this.onAllLocationsSelect} fieldValue="all"
                                                                id="loc_all">{t('l.selection.all')}</RadioButton>}
                              />
                              <FieldControl
                                strict={false}
                                name="locationsSelection"
                                render={(props) => <RadioButton {...props}
                                                                fieldValue="selection" id="loc_sel">
                                  {t('l.selection.selected')}</RadioButton>}
                              />
                            </div>
                            {
                              this.form.controls.locationsSelection.value === 'selection'
                              && (
                                <div className="d-flex row">
                                  <FieldControl
                                    name="locations"
                                    render={(v) => (
                                      <MultiSelect
                                        {...v}
                                        selectedAttributeName="deskCode"
                                        sortAttributeName="name"
                                        handleValueChange={(val: any) => this.handleLocationSelectValueChange(val)}
                                        filterAttributes={[ 'name', 'deskCode' ]}
                                        primaryAtr="name"
                                        secondaryAtr="deskCode"
                                      />
                                    )}
                                  />
                                </div>
                              )
                            }
                          </div>
                        </>
                      )
                    }
                  </div>
                </div>
                <SectionTitle title={t('section_title.on_request_rule.car_classes').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4"/>
                  <div className="col-8 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedOnRequestRule && !showForm
                      && (
                        <div className="form-control-static">
                          {getCarClassesList(
                            this.props.selectedOnRequestRule.onRequestRuleCarClasses,
                            this.props.carClasses,
                            this.props.t('v.all_car_classes'),
                            this.props.history
                          )}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <>
                          <div className="d-flex flex-column flex-wrap">
                            <div className="pb-1">
                              <FieldControl
                                strict={false}
                                name="carClassSelection"
                                render={(props) => <RadioButton {...props}
                                                                onClick={this.onAllCarClassesSelect}
                                                                fieldValue="all" id="car_all">
                                  {t('l.selection.all')}</RadioButton>}
                              />
                              <FieldControl
                                strict={false}
                                name="carClassSelection"
                                render={(props) => <RadioButton {...props}
                                                                fieldValue="selection" id="car_sel">
                                  {t('l.selection.selected')}
                                </RadioButton>}
                              />
                            </div>
                            {
                              this.form.controls.carClassSelection.value === 'selection'
                              && (
                                <FieldControl
                                  name="carClasses"
                                  render={(v) => (
                                    <MultiSelect
                                      {...v}
                                      selectedAttributeName="acrissCode"
                                      sortAttributeName="acrissCode"
                                      handleValueChange={(val: any) => this.handleCarClassSelectValueChange(val)}
                                      filterAttributes={[ 'name', 'acrissCode' ]}
                                      primaryAtr="acrissCode"
                                      secondaryAtr="name"
                                    />
                                  )}
                                />
                              )
                            }
                          </div>
                        </>
                      )
                    }
                  </div>
                </div>
                {showForm && (
                  <div className="row">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}
                      </button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                        {t('b.save_Generic save button translation').toUpperCase()}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          />
          {this.props.selectedOnRequestRule && !showForm && (
            <>
              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-8">
                  <div className="d-flex row info-container">
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedOnRequestRule.createdBy ?? 'System'}{this.props.selectedOnRequestRule.createdAt ? ` @ ${formatDateTime(this.props.selectedOnRequestRule.createdAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedOnRequestRule.modifiedBy ?? 'System'}{this.props.selectedOnRequestRule.modifiedAt ? ` @ ${formatDateTime(this.props.selectedOnRequestRule.modifiedAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <Link to={`/rates/onrequestrules/${this.props.selectedOnRequestRule.id}/audit`}
                            className="btn btn-text-only"
                            target="_blank"
                            rel="noopener noreferrer">
                        {t('b.car_model.view_audit_log')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <ConfirmationModal
          title={t('modal_text.on_request_rule.confirm_removal.title')}
          content={t('modal_text.on_request_rule.confirm_removal.content')}
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedOnRequestRule: state.rates.onRequestRules.selectedOnRequestRule,
  postSuccess: state.rates.onRequestRules.postSuccess,
  carClasses: state.core.carClasses.acrissCodes,
  desks: state.setup.desks.desks,
  lastFetch: state.rates.onRequestRules.lastFetch,
  user: state.authentication.user
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, OnRequestRuleActionTypes>
) => ({
  getSelectedOnRequestRule: (id?: number) => dispatch(getOnRequestRule(id)),
  editOnRequestRule: (id: number, request: OnRequestRuleRequest) => dispatch(editOnRequestRule(id, request)),
  addNewOnRequestRule: (request: OnRequestRuleRequest) => dispatch(addNewONRequestRule(request)),
  deleteOnRequestRule: (id: number) => dispatch(deleteOnRequestRule(id)),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getDeskList: () => dispatch(getDeskList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(OnRequestRuleDetails));
